import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  getMarketplaceCategories,
  getMarketplaceCategoriesHierarchy,
  createMarketplaceCategory,
  updateMarketplaceCategory,
  deleteMarketplaceCategory,
} from "@/lib/marketplaceAPI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Plus,
  Trash2,
  Edit,
  Folder,
  FolderTree,
  AlertTriangle,
  Loader2,
  Tag,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";

// Interfaz para las categorías de marketplace
interface MarketplaceCategory {
  id: string;
  name: string;
  slug: string;
  parent_id: string | null;
  description?: string;
  children?: MarketplaceCategory[];
}

// Interfaz para adaptar Category a MarketplaceCategory
interface CategoryAdapter {
  id: number;
  name: string;
  slug: string;
  parentId: number | null;
  description?: string;
  children?: CategoryAdapter[];
}

export function UsedCategoriesTab() {
  const { toast } = useToast();

  // Estados
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState<Partial<MarketplaceCategory> | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"flat" | "hierarchy">("flat");

  // Queries
  const {
    data: categories,
    isLoading: isLoadingCategories,
    refetch: refetchCategories,
  } = useQuery({
    queryKey: ["marketplace-categories-admin"],
    queryFn: () => getMarketplaceCategories(),
  });

  const {
    data: categoriesHierarchy,
    isLoading: isLoadingHierarchy,
  } = useQuery({
    queryKey: ["marketplace-categories-hierarchy-admin"],
    queryFn: () => getMarketplaceCategoriesHierarchy(),
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: {
      name: string;
      slug: string;
      parent_id?: string | null;
      description?: string;
    }) => createMarketplaceCategory(data),
    onSuccess: () => {
      toast({
        title: "Categoría creada",
        description: "La categoría ha sido creada correctamente",
      });
      refetchCategories();
      setIsEditDialogOpen(false);
      setCurrentCategory(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Ha ocurrido un error al crear la categoría",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: {
      id: string;
      data: {
        name?: string;
        slug?: string;
        parent_id?: string | null;
        description?: string;
      }
    }) => updateMarketplaceCategory(id, data),
    onSuccess: () => {
      toast({
        title: "Categoría actualizada",
        description: "La categoría ha sido actualizada correctamente",
      });
      refetchCategories();
      setIsEditDialogOpen(false);
      setCurrentCategory(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Ha ocurrido un error al actualizar la categoría",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteMarketplaceCategory(id),
    onSuccess: () => {
      toast({
        title: "Categoría eliminada",
        description: "La categoría ha sido eliminada correctamente",
      });
      refetchCategories();
      setIsDeleting(false);
      setDeleteId(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Ha ocurrido un error al eliminar la categoría",
        variant: "destructive",
      });
      setIsDeleting(false);
    },
  });

  // Handlers
  const handleCreateNew = () => {
    setCurrentCategory({
      name: "",
      slug: "",
      parent_id: null,
      description: "",
    });
    setIsEditDialogOpen(true);
  };

  const handleEdit = (category: MarketplaceCategory) => {
    setCurrentCategory(category);
    setIsEditDialogOpen(true);
  };

  const handleSave = () => {
    if (!currentCategory?.name) {
      toast({
        title: "Campo requerido",
        description: "El nombre de la categoría es obligatorio",
        variant: "destructive",
      });
      return;
    }

    // Si el slug está vacío, lo generamos a partir del nombre
    if (!currentCategory.slug) {
      currentCategory.slug = currentCategory.name
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^\w\s]/gi, "")
        .replace(/\s+/g, "-");
    }

    if ("id" in currentCategory && currentCategory.id) {
      updateMutation.mutate({
        id: currentCategory.id,
        data: {
          name: currentCategory.name,
          slug: currentCategory.slug,
          parent_id: currentCategory.parent_id,
          description: currentCategory.description,
        },
      });
    } else {
      createMutation.mutate({
        name: currentCategory.name,
        slug: currentCategory.slug!,
        parent_id: currentCategory.parent_id,
        description: currentCategory.description,
      });
    }
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
    setIsDeleting(true);
  };

  const confirmDelete = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
    }
  };

  // Renderizado de la tabla plana
  const renderFlatTable = () => {
    if (isLoadingCategories) {
      return (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[#FCAA0D]" />
          <span className="ml-3 text-white/60">Cargando categorías...</span>
        </div>
      );
    }

    if (!categories || categories.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
          <div className="bg-white/5 p-4 rounded-full mb-4">
            <FolderTree className="h-12 w-12 text-white/40" />
          </div>
          <h3 className="text-xl font-medium text-white mb-2">No hay categorías</h3>
          <p className="text-white/60 max-w-md mb-6">
            No se encontraron categorías para productos de ocasión. Añade tu primera categoría para organizar tus productos.
          </p>
          <Button
            variant="default"
            onClick={handleCreateNew}
            className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black font-medium"
          >
            <Plus className="mr-2 h-4 w-4" />
            Añadir categoría
          </Button>
        </div>
      );
    }

    // Convertir las categorías de la API al formato que esperamos
    const formattedCategories: MarketplaceCategory[] = categories.map(cat => ({
      id: String(cat.id),
      name: cat.name,
      slug: cat.slug || '',
      parent_id: cat.parentId ? String(cat.parentId) : null,
      description: cat.description
    }));

    return (
      <div className="rounded-md overflow-hidden border border-white/10">
        <Table className="w-full">
          <TableHeader className="bg-[#1A1A1C]">
            <TableRow className="hover:bg-transparent border-white/10">
              <TableHead className="text-white/70 font-medium">Nombre</TableHead>
              <TableHead className="text-white/70 font-medium">Slug</TableHead>
              <TableHead className="text-white/70 font-medium">Categoría padre</TableHead>
              <TableHead className="text-white/70 font-medium text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {formattedCategories.map((category) => (
              <TableRow key={category.id} className="hover:bg-white/5 border-white/10">
                <TableCell className="font-medium text-white">{category.name}</TableCell>
                <TableCell className="text-white/80 font-mono text-sm">{category.slug}</TableCell>
                <TableCell className="text-white/80">
                  {category.parent_id ? (
                    formattedCategories.find((c) => c.id === category.parent_id)?.name || "N/A"
                  ) : (
                    <span className="text-white/40">-</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(category)}
                      className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(category.id)}
                      className="h-8 w-8 text-white/70 hover:text-red-500 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  // Renderizado de la tabla jerárquica
  const renderHierarchyTable = () => {
    if (isLoadingHierarchy) {
      return (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[#FCAA0D]" />
          <span className="ml-3 text-white/60">Cargando jerarquía de categorías...</span>
        </div>
      );
    }

    if (!categoriesHierarchy || categoriesHierarchy.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
          <div className="bg-white/5 p-4 rounded-full mb-4">
            <FolderTree className="h-12 w-12 text-white/40" />
          </div>
          <h3 className="text-xl font-medium text-white mb-2">No hay categorías</h3>
          <p className="text-white/60 max-w-md mb-6">
            No se encontraron categorías para productos de ocasión. Añade tu primera categoría para organizar tus productos.
          </p>
          <Button
            variant="default"
            onClick={handleCreateNew}
            className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black font-medium"
          >
            <Plus className="mr-2 h-4 w-4" />
            Añadir categoría
          </Button>
        </div>
      );
    }

    // Convertir las categorías de la API al formato que esperamos
    const formatCategories = (cats: CategoryAdapter[]): MarketplaceCategory[] => {
      return cats.map(cat => ({
        id: String(cat.id),
        name: cat.name,
        slug: cat.slug || '',
        parent_id: cat.parentId ? String(cat.parentId) : null,
        description: cat.description,
        children: cat.children ? formatCategories(cat.children) : undefined
      }));
    };

    const formattedCategoriesHierarchy = formatCategories(categoriesHierarchy as CategoryAdapter[]);

    const renderCategories = (categories: MarketplaceCategory[], level = 0) => {
      return (
        <>
          {categories.map((category) => (
            <React.Fragment key={category.id}>
              <TableRow className="hover:bg-white/5 border-white/10">
                <TableCell>
                  <div className="flex items-center">
                    <div
                      className="flex-shrink-0"
                      style={{ width: `${level * 20}px` }}
                    />
                    <div className="mr-2 p-1 rounded-md bg-white/5">
                      <Folder className="h-4 w-4 text-white/70" />
                    </div>
                    <span className="font-medium text-white">{category.name}</span>
                  </div>
                </TableCell>
                <TableCell className="text-white/80 font-mono text-sm">{category.slug}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(category)}
                      className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(category.id)}
                      className="h-8 w-8 text-white/70 hover:text-red-500 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
              {category.children && category.children.length > 0 && (
                renderCategories(category.children, level + 1)
              )}
            </React.Fragment>
          ))}
        </>
      );
    };

    return (
      <div className="rounded-md overflow-hidden border border-white/10">
        <Table className="w-full">
          <TableHeader className="bg-[#1A1A1C]">
            <TableRow className="hover:bg-transparent border-white/10">
              <TableHead className="text-white/70 font-medium">Nombre</TableHead>
              <TableHead className="text-white/70 font-medium">Slug</TableHead>
              <TableHead className="text-white/70 font-medium text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {renderCategories(formattedCategoriesHierarchy)}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white mb-1">Categorías de Maquinaria Usada</h2>
          <p className="text-white/60">
            Gestiona las categorías para organizar los productos de ocasión ({categories?.length || 0} categorías en total)
          </p>
        </div>
        <Button
          onClick={handleCreateNew}
          className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black font-medium"
        >
          <Plus className="mr-2 h-4 w-4" />
          Nueva categoría
        </Button>
      </div>

      <div className="mb-6">
        <Card className="bg-[#111213] border-white/10 shadow-md">
          <CardHeader className="pb-3">
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="text-white text-lg">Vista</CardTitle>
                <CardDescription className="text-white/60">
                  Selecciona cómo deseas visualizar las categorías
                </CardDescription>
              </div>
              <div className="flex rounded-md overflow-hidden border border-white/10">
                <Button
                  variant={viewMode === "flat" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setViewMode("flat")}
                  className={viewMode === "flat"
                    ? "bg-[#FCAA0D] text-black rounded-none"
                    : "bg-transparent text-white hover:text-white hover:bg-white/5 rounded-none border-0"}
                >
                  Lista plana
                </Button>
                <Button
                  variant={viewMode === "hierarchy" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setViewMode("hierarchy")}
                  className={viewMode === "hierarchy"
                    ? "bg-[#FCAA0D] text-black rounded-none"
                    : "bg-transparent text-white hover:text-white hover:bg-white/5 rounded-none border-0"}
                >
                  Jerarquía
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>
      </div>

      {viewMode === "flat" ? renderFlatTable() : renderHierarchyTable()}

      {/* Diálogo para añadir/editar categorías */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="bg-[#111213] text-white border-white/10">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white">
              {currentCategory?.id ? "Editar categoría" : "Añadir nueva categoría"}
            </DialogTitle>
            <DialogDescription className="text-white/60">
              {currentCategory?.id
                ? "Modifica los detalles de la categoría"
                : "Crea una nueva categoría para organizar tus productos de ocasión"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-white mb-1.5 block">Nombre *</Label>
              <Input
                id="name"
                value={currentCategory?.name || ""}
                onChange={(e) => setCurrentCategory({ ...currentCategory, name: e.target.value })}
                placeholder="Ej: CORTADORAS"
                className="bg-[#1A1A1C] border-white/10 focus-visible:ring-[#FCAA0D] text-white"
              />
            </div>

            <div>
              <Label htmlFor="slug" className="text-white mb-1.5 block">Slug</Label>
              <Input
                id="slug"
                value={currentCategory?.slug || ""}
                onChange={(e) => setCurrentCategory({ ...currentCategory, slug: e.target.value })}
                placeholder="Ej: cortadoras (se genera automáticamente)"
                className="bg-[#1A1A1C] border-white/10 focus-visible:ring-[#FCAA0D] text-white"
              />
              <p className="text-sm text-white/40 mt-1">
                Identificador único para URL. Si se deja en blanco, se generará automáticamente.
              </p>
            </div>

            <div>
              <Label htmlFor="parent" className="text-white mb-1.5 block">Categoría padre</Label>
              <Select
                value={currentCategory?.parent_id || ""}
                onValueChange={(value) => setCurrentCategory({ ...currentCategory, parent_id: value === "null" ? null : value })}
              >
                <SelectTrigger className="bg-[#1A1A1C] border-white/10 focus:ring-[#FCAA0D] text-white">
                  <SelectValue placeholder="Selecciona una categoría padre (opcional)" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1C] border-white/10 text-white">
                  <SelectItem value="null" className="focus:bg-white/10">Ninguna (Categoría principal)</SelectItem>
                  {categories?.map((category) => {
                    // Convertir la categoría al formato que esperamos
                    const cat: MarketplaceCategory = {
                      id: String(category.id),
                      name: category.name,
                      slug: category.slug || '',
                      parent_id: category.parentId ? String(category.parentId) : null,
                      description: category.description
                    };

                    // No permitir seleccionar la categoría actual como padre
                    return cat.id !== currentCategory?.id && (
                      <SelectItem key={cat.id} value={cat.id} className="focus:bg-white/10">
                        {cat.name}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="description" className="text-white mb-1.5 block">Descripción</Label>
              <Textarea
                id="description"
                value={currentCategory?.description || ""}
                onChange={(e) => setCurrentCategory({ ...currentCategory, description: e.target.value })}
                placeholder="Describe esta categoría (opcional)"
                className="bg-[#1A1A1C] border-white/10 focus-visible:ring-[#FCAA0D] text-white"
              />
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row sm:justify-between gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsEditDialogOpen(false);
                setCurrentCategory(null);
              }}
              className="border-white/10 text-white hover:text-white hover:bg-white/5"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={createMutation.isPending || updateMutation.isPending}
              onClick={handleSave}
              className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black font-medium"
            >
              {(createMutation.isPending || updateMutation.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {currentCategory?.id ? "Guardar cambios" : "Crear categoría"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo de confirmación para eliminar */}
      <Dialog open={isDeleting} onOpenChange={setIsDeleting}>
        <DialogContent className="max-w-md bg-[#111213] text-white border-white/10">
          <DialogHeader>
            <div className="flex justify-center mb-2">
              <div className="p-3 rounded-full bg-red-500/10">
                <AlertTriangle className="h-6 w-6 text-red-500" />
              </div>
            </div>
            <DialogTitle className="text-center text-xl font-bold text-white">
              ¿Eliminar categoría?
            </DialogTitle>
            <DialogDescription className="text-center text-white/60">
              Esta acción no se puede deshacer. Si hay productos asociados a esta categoría, podrían verse afectados.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col sm:flex-row sm:justify-center gap-2 mt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsDeleting(false);
                setDeleteId(null);
              }}
              className="border-white/10 text-white hover:text-white hover:bg-white/5"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={confirmDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {deleteMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
